import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/auth";

/**
 * AdminSlotSelect — dropdown listing every intro slot in the calendar
 * (open + booked) so an admin can override a vendor's/couple's chosen
 * appointment when editing an application.
 *
 * Props:
 *  - date, time: currently selected slot (may be "" if none picked yet)
 *  - onChange({ date, time }): fires whenever the admin picks a slot
 *  - testId: prefix for data-testid attributes
 */
const formatDateLabel = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const AdminSlotSelect = ({ date, time, onChange, testId = "admin-slot" }) => {
  const [slots, setSlots] = useState(null);
  const [error, setError] = useState(null);

  const fetchSlots = async () => {
    try {
      const r = await api.get("/admin/slots");
      setSlots(r.data || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load slots");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/slots");
        if (!cancelled) setSlots(r.data || []);
      } catch (e) {
        if (!cancelled)
          setError(e?.response?.data?.detail || "Could not load slots");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sorted = useMemo(
    () =>
      (slots || [])
        .slice()
        .sort((a, b) =>
          `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`)
        ),
    [slots]
  );

  const currentValue = date && time ? `${date}|${time}` : "";

  return (
    <div className="space-y-1" data-testid={testId}>
      <select
        data-testid={`${testId}-select`}
        value={currentValue}
        onFocus={fetchSlots}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) {
            onChange({ date: "", time: "" });
            return;
          }
          const [d, t] = v.split("|");
          onChange({ date: d, time: t });
        }}
        className="w-full h-10 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
      >
        <option value="">— No appointment —</option>
        {/* Preserve the existing selection even if the slot isn't in the
            calendar collection (e.g. legacy manual entry). */}
        {currentValue &&
          !sorted.find((s) => `${s.date}|${s.time}` === currentValue) && (
            <option value={currentValue}>
              {formatDateLabel(date)} · {time} (current)
            </option>
          )}
        {sorted.map((s) => {
          const value = `${s.date}|${s.time}`;
          const bookedTag = s.booked_by ? " · BOOKED" : "";
          const kindTag =
            s.kind === "vendor_intro_open" || s.kind === "vendor_intro"
              ? " (vendor)"
              : s.kind === "couple_intro_open" || s.kind === "couple_intro"
              ? " (couple)"
              : s.kind === "admin_held"
              ? " (held)"
              : "";
          return (
            <option key={s.id} value={value}>
              {formatDateLabel(s.date)} · {s.time}
              {kindTag}
              {bookedTag}
            </option>
          );
        })}
      </select>
      {error && (
        <p className="text-[11px] text-red-700" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
      {!error && !slots && (
        <p className="text-[11px] text-[var(--ld-ink-muted)]">
          Loading slots…
        </p>
      )}
    </div>
  );
};

export default AdminSlotSelect;
